import { useTheme } from '@emotion/react';
import { Grid } from '@mui/material';
import AryCurrencyAutoComplete from 'framework/base/components/AryCurrencyAutoComplete';
import AryLockableTextField from 'framework/base/components/AryLockableTextField';
import useCurrencyDecimalPlacesDeprecated from 'framework/base/hooks/useCurrencyDecimalPlacesDeprecated';
import useTradeRateValidation from 'framework/base/hooks/useTradeRateValidation';
import AryTriplexComponentsLock from 'framework/desktop/triplex-component/AryTriplexComponentsLock';
import {
  enforceSameCurrencyTriplexConsistency,
  isNeutralTradeRate,
  isSameCurrencyPair
} from 'framework/desktop/triplex-component/amountConsistency';
import useAryCurrencyManagement from 'framework/desktop/triplex-component/useAryCurrencyManagement';
import {
  calculateEquivalentBaseOnInitialAndTradeRate,
  calculateInitialBaseOnEquivalentAndTradeRate,
  calculateTradeRateBaseOnInitialAndEquivalent
} from 'framework/desktop/triplex-component/utils';
import useFrameworkTranslation from 'framework/language/useFrameworkTranslation';
import React, { useEffect, useState } from 'react';
import ProfitRate from './ProfitRate';
import SystemRate from './SystemRate';

const currencyGroupComponents = [
  'initialAmountValue',
  'equivalentAmountValue',
  'tradeRate'
];

const AryTriplex = ({
  transactionId,
  initialProps,
  equivalentProps,
  tradeRateProps,
  profitRateProps,
  initialAmountDisabled,
  equivalentAmountDisabled,
  hideLocks,
  spotRate,
  profitRate,
  isRateMinusEnteredRate,
  disableValidation,
  ...others
}) => {
  const theme = useTheme();
  const { t } = useFrameworkTranslation();

  const [anchorEl, setAnchorEl] = useState(null);
  const [popOver, setPopOver] = useState(false);
  const [lockedLabel, setLockedLabel] = useState({
    value: 'equivalent',
    label: t('Sell Amount')
  });

  const shouldIgnoreTradeRateError = Boolean(tradeRateProps?.disableValidation);

  const [calculationRate, isDivision, getCurrentCalculationRateAndIsDivision] =
    useAryCurrencyManagement({
      sourceCurrencyCode: initialProps?.currencyCode,
      destinationCurrencyCode: equivalentProps?.currencyCode
    });

  useEffect(() => {
    if (initialAmountDisabled && equivalentAmountDisabled) {
      setLockedLabel({
        value: 'tradeRate',
        label: t('Trade Rate')
      });
    }

    if (initialAmountDisabled) {
      setLockedLabel({
        value: 'equivalent',
        label: t('Sell Amount')
      });
    }

    if (equivalentAmountDisabled) {
      setLockedLabel({
        value: 'initial',
        label: t('Buy Amount')
      });
    }

    if (!initialAmountDisabled && !equivalentAmountDisabled) {
    // set initial value if we don't have any disabled field
      setLockedLabel({
        value: 'equivalent',
        label: t('Sell Amount')
      });
    }
  }, [initialAmountDisabled, equivalentAmountDisabled]);

  const conditionalProps = (field) => {
    if (lockedLabel.value === field) {
      return {
        changecalculationmethod: true,
        editLockFieldChangeHandler: (newval) => {
          setAnchorEl(newval);
          setPopOver(true);
        }
      };
    }
    return {};
  };

  const lockableComponents = [
    !initialAmountDisabled && {
      value: 'initial',
      label: initialProps?.amountLabel
    },
    !equivalentAmountDisabled && {
      value: 'equivalent',
      label: equivalentProps?.amountLabel
    },
    {
      value: 'tradeRate',
      label: 'Trade Rate'
    }
  ].filter(Boolean);

  const [isTradeRateValid, tradeRateLabel] = useTradeRateValidation({
    sourceCurrencyCode: initialProps?.currencyCode,
    destinationCurrencyCode: equivalentProps?.currencyCode,
    tradeRate: tradeRateProps?.tradeRate
  });
  const initialCurrencyDecimalPlaces = useCurrencyDecimalPlacesDeprecated(
    initialProps?.currencyCode,
    others?.branchId || null,
    !!others?.branchId
  );
  const equivalentCurrencyDecimalPlaces = useCurrencyDecimalPlacesDeprecated(
    equivalentProps?.currencyCode,
    others?.branchId || null,
    !!others?.branchId
  );

  useEffect(() => {
    tradeRateProps?.onIsTradeRateValidChange(isTradeRateValid);
  }, [isTradeRateValid]);

  useEffect(() => {
    // The shared triplex rule: the same currency on both sides means "no
    // conversion", so the trade rate is 1 and the two amounts are the same
    // money. It is enforced here - before the `isDivision` guard, outside the
    // `lockedLabel` branches and without requiring both amounts to be filled -
    // because it needs no conversion data at all. Waiting for the asynchronous
    // currency-calculation query, or for the other amount to be non empty, was
    // exactly the window in which fast typing could leave the pair out of sync.
    const isSameCurrencyHandled = enforceSameCurrencyTriplexConsistency({
      initialCurrencyCode: initialProps?.currencyCode,
      initialAmountValue: initialProps?.amountValue,
      equivalentCurrencyCode: equivalentProps?.currencyCode,
      equivalentAmountValue: equivalentProps?.amountValue,
      tradeRate: tradeRateProps?.tradeRate,
      lockedLabel: lockedLabel.value,
      setValues: {
        tradeRateOnChange: tradeRateProps?.tradeRateOnChange,
        initialAmountValueOnChange: initialProps?.amountValueOnChange,
        equivalentAmountValueOnChange: equivalentProps?.amountValueOnChange
      }
    });
    // Same currency: there is nothing left for the conversion branches to do,
    // they only re-derive the very same mirror through `utils.js`.
    if (isSameCurrencyHandled) return;

    if (isDivision === null) return;

    if (lockedLabel.value === 'tradeRate') {
      if (
        initialProps?.currencyCode &&
        initialProps?.amountValue &&
        equivalentProps?.currencyCode &&
        equivalentProps?.amountValue
      ) {
        const [currentCalculationRate, currentIsDivision] =
          getCurrentCalculationRateAndIsDivision();
        calculateTradeRateBaseOnInitialAndEquivalent({
          initialCurrencyCode: initialProps?.currencyCode,
          initialAmountValue: initialProps?.amountValue,
          equivalentCurrencyCode: equivalentProps?.currencyCode,
          equivalentAmountValue: equivalentProps?.amountValue,
          setValues: {
            tradeRateOnChange: tradeRateProps?.tradeRateOnChange,
            equivalentAmountValueOnChange: equivalentProps?.amountValueOnChange
          },
          calculationRate: currentCalculationRate || calculationRate,
          isDivision: currentIsDivision || isDivision,
          initialAmountDisabled
        });
      }
    } else if (lockedLabel.value === 'initial') {
      if (
        initialProps?.currencyCode &&
        equivalentProps?.currencyCode &&
        equivalentProps?.amountValue &&
        tradeRateProps?.tradeRate
      ) {
        const [currentCalculationRate, currentIsDivision] =
          getCurrentCalculationRateAndIsDivision();
        calculateInitialBaseOnEquivalentAndTradeRate({
          initialCurrencyCode: initialProps?.currencyCode,
          equivalentCurrencyCode: equivalentProps?.currencyCode,
          equivalentAmountValue: equivalentProps?.amountValue,
          tradeRate: tradeRateProps?.tradeRate,
          setValues: {
            tradeRateOnChange: tradeRateProps?.tradeRateOnChange,
            initialAmountValueOnChange: initialProps?.amountValueOnChange
          },
          calculationRate: currentCalculationRate || calculationRate,
          isDivision: currentIsDivision || isDivision,
          initialAmountDisabled,
          initialCurrencyDecimalPlaces
        });
      }
    } else if (lockedLabel.value === 'equivalent') {
      if (
        initialProps?.currencyCode &&
        equivalentProps?.currencyCode &&
        initialProps?.amountValue &&
        tradeRateProps?.tradeRate
      ) {
        const [currentCalculationRate, currentIsDivision] =
          getCurrentCalculationRateAndIsDivision();
        calculateEquivalentBaseOnInitialAndTradeRate({
          initialCurrencyCode: initialProps?.currencyCode,
          equivalentCurrencyCode: equivalentProps?.currencyCode,
          initialAmountValue: initialProps?.amountValue,
          tradeRate: tradeRateProps?.tradeRate,
          setValues: {
            tradeRateOnChange: tradeRateProps?.tradeRateOnChange,
            equivalentAmountValueOnChange: equivalentProps?.amountValueOnChange
          },
          calculationRate: currentCalculationRate || calculationRate,
          isDivision: currentIsDivision || isDivision,
          equivalentCurrencyDecimalPlaces
        });
      }
    }
  }, [
    initialProps?.currencyCode,
    initialProps?.amountValue,
    equivalentProps?.currencyCode,
    equivalentProps?.amountValue,
    tradeRateProps?.tradeRate,
    isDivision,
    lockedLabel
  ]);

  return (
    <>
      <Grid container>
        {initialProps?.isHidden && null}
        {!initialProps?.isHidden && (
          <Grid
            item
            xs={8}
            padding="1rem"
            bgcolor={theme.wrapper.background.paper}
            borderRadius="0.5rem"
            borderRight={`${theme.icon.disable.color} 0.0625rem dashed`}
          >
            <Grid container spacing={5}>
              <Grid item xs={6}>
                <AryCurrencyAutoComplete
                  value={
                    initialProps?.initialFixedCurrencyCode ||
                    initialProps?.currencyCode
                  }
                  onChange={initialProps?.currencyCodeOnChange}
                  label={`${t(initialProps?.currencyLabel) || t('currency')}${
                    initialProps?.currencyCodeRequired ? ' *' : ''
                  }`}
                  disabled={
                    lockedLabel.value === currencyGroupComponents[0] ||
                    others.initialCurrencyDisabled ||
                    initialProps?.initialFixedCurrencyCode
                  }
                  handleChangeDecimalPlaces={others.handleChangeDecimalPlaces}
                  excludedCurrencyCodes={initialProps?.excludedCurrencyCodes}
                  onlyCurrencyCodes={initialProps?.initialOnlyCurrencyCodes}
                  // getMirrorCurrencies={getMirrorCurrencies}
                  branchId={others.branchId}
                  maxheight={others.initialCurrencyMaxHeight}
                  apiType={others.apiType}
                  // handleChangeCurrencyCounts={handleChangeCurrencyCounts}
                  style={{
                    control: {
                      borderRadius: '0.25rem',
                      backgroundColor: `${theme.chip.blue.background} !important`
                    },
                    dropdownIndicator: {
                      paddingTop: '0',
                      paddingBottom: '0',
                      ...initialProps?.currencyCodeSx?.dropdownIndicator
                    }
                  }}
                  error={initialProps?.currencyCodeError}
                  helperText={initialProps?.currencyCodeHelperText}
                  onFocus={initialProps?.onFocus && initialProps?.onFocus}
                  {...initialProps?.currencyCodeAutoCompleteProps}
                />
              </Grid>
              <Grid item xs={6}>
                <AryLockableTextField
                  fullWidth
                  removeAdditionalPadding
                  decimalPlaces={
                    initialCurrencyDecimalPlaces < 0
                      ? 0
                      : initialCurrencyDecimalPlaces
                  }
                  label={`${initialProps?.initialAmountLabel || t('amount')}`}
                  disabled={
                    lockedLabel.value === 'initial' ||
                    !(
                      initialProps?.currencyCode ||
                      initialProps?.initialFixedCurrencyCode
                    ) ||
                    initialAmountDisabled
                  }
                  value={initialProps?.amountValue}
                  currencyCode={
                    initialProps?.initialProps?.initialFixedCurrencyCode ||
                    initialProps?.currencyCode
                  }
                  style={{
                    borderRadius: '0.25rem'
                  }}
                  onChange={(event) => {
                    initialProps?.amountValueOnChange(event?.numberValue);
                  }}
                  onBlur={initialProps?.onBlur}
                  error={initialProps?.amountError}
                  helperText={initialProps?.amountHelperText}
                  sx={initialProps?.amountSx}
                  hideEditIcon={
                    (initialAmountDisabled && equivalentAmountDisabled) ||
                    hideLocks
                  }
                  {...conditionalProps('initial')}
                />
              </Grid>
            </Grid>
          </Grid>
        )}
        {initialProps?.isHidden ?
          (
            <>
              <Grid
                item
                xs={8}
                padding="1rem"
                paddingTop={initialProps?.isHidden ? '1rem' : '0'}
                bgcolor={theme.wrapper.background.paper}
                borderRadius="0.5rem"
                borderRight={`${theme.icon.disable.color} 0.0625rem dashed`}
              >
                <Grid container spacing={5}>
                  <Grid item xs={6}>
                    <AryCurrencyAutoComplete
                      value={
                        equivalentProps?.equivalentFixedCurrencyCode ||
                        equivalentProps?.currencyCode
                      }
                      onChange={equivalentProps?.currencyCodeOnChange}
                      label={`${t(equivalentProps?.currencyLabel_) || t('currency')}${
                        equivalentProps?.currencyCodeRequired ? ' *' : ''
                      }`}
                      disabled={
                        others.equivalentCurrencyDisabled ||
                        equivalentProps?.equivalentFixedCurrencyCode
                      }
                      handleChangeDecimalPlaces={others.handleChangeDecimalPlaces}
                      excludedCurrencyCodes={equivalentProps?.excludedCurrencyCodes}
                      onlyCurrencyCodes={equivalentProps?.equivalentOnlyCurrencyCodes}
                      // getMirrorCurrencies={getMirrorCurrencies}
                      branchId={others.branchId}
                      maxheight={others.equivalentCurrencyMaxHeight}
                      apiType={others.apiType}
                      // handleChangeCurrencyCounts={handleChangeCurrencyCounts}
                      // error={error}
                      style={{
                        control: {
                          borderRadius: '0.25rem',
                          backgroundColor: `${theme.chip.blue.background} !important`,
                          ...equivalentProps?.currencyCodeSx?.control
                        },
                        dropdownIndicator: {
                          paddingTop: '0',
                          paddingBottom: '0',
                          ...equivalentProps?.currencyCodeSx?.dropdownIndicator
                        }
                      }}
                      {...equivalentProps?.currencyCodeAutoCompleteProps}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <AryLockableTextField
                      fullWidth
                      removeAdditionalPadding
                      decimalPlaces={
                        equivalentCurrencyDecimalPlaces < 0
                          ? 0
                          : equivalentCurrencyDecimalPlaces
                      }
                      label={`${
                        equivalentProps?.equivalentAmountLabel || t('amount')
                      }`}
                      disabled={
                        lockedLabel.value === 'equivalent' ||
                        !(
                          equivalentProps?.currencyCode ||
                          equivalentProps?.equivalentFixedCurrencyCode
                        ) ||
                        equivalentAmountDisabled ||
                        (isNeutralTradeRate(tradeRateProps?.tradeRate) &&
                          isSameCurrencyPair(
                            initialProps?.currencyCode,
                            equivalentProps?.currencyCode
                          ))
                      }
                      error={equivalentProps?.amountError}
                      value={equivalentProps?.amountValue}
                      currencyCode={
                        equivalentProps?.equivalentFixedCurrencyCode ||
                        equivalentProps?.currencyCode
                      }
                      onChange={(event) => {
                        equivalentProps?.amountValueOnChange(event?.numberValue);
                      }}
                      style={{
                        borderRadius: '0.25rem'
                      }}
                      onBlur={equivalentProps?.onBlur}
                      helperText={equivalentProps?.amountHelperText}
                      sx={equivalentProps?.amountSx}
                      hideEditIcon={
                        (initialAmountDisabled && equivalentAmountDisabled) ||
                        hideLocks
                      }
                      {...conditionalProps('equivalent')}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid
                item
                xs={4}
                padding="1rem"
                bgcolor={theme.wrapper.background.paper}
                borderRadius="0.5rem"
              >
            <Grid container spacing={5}>
              <Grid item xs={12}>
                <AryLockableTextField
                  name="tradeRate"
                  label={tradeRateLabel || t('Trade Rate')}
                  value={tradeRateProps?.tradeRate}
                  decimalPlaces={6}
                  fullWidth
                  disabled={
                    lockedLabel.value === 'tradeRate' ||
                    initialProps?.currencyCode ===
                      equivalentProps?.currencyCode ||
                    (initialAmountDisabled && equivalentAmountDisabled)
                  }
                  onChange={(event) => {
                    tradeRateProps?.tradeRateOnChange(event?.numberValue);
                  }}
                  onBlur={() => {
                    tradeRateProps?.onBlur();
                    if (
                      lockedLabel.value === currencyGroupComponents[0] &&
                      tradeRateProps?.tradeRate === 1
                    ) {
                      equivalentProps.equivalentCurrencyCodeOnChange();
                      equivalentProps.equivalentAmountValueOnChange();
                    }
                    if (
                      lockedLabel.value === currencyGroupComponents[2] &&
                      tradeRateProps?.tradeRate === 1
                    ) {
                      initialProps.initialCurrencyCodeOnChange();
                      initialProps.initialAmountValueOnChange();
                    }
                  }}
                  error={
                    !shouldIgnoreTradeRateError &&
                    ((initialProps?.amountValue &&
                      equivalentProps?.amountValue &&
                      tradeRateProps?.tradeRate <= 0) ||
                    (initialProps?.amountValue &&
                      equivalentProps?.amountValue &&
                      !isTradeRateValid))
                  }
                  helperText={
                    !shouldIgnoreTradeRateError && ((initialProps?.amountValue &&
                      equivalentProps?.amountValue &&
                      tradeRateProps?.tradeRate <= 0 &&
                      t('positiveTradeRate')) ||
                    (initialProps?.amountValue &&
                      equivalentProps?.amountValue &&
                      !isTradeRateValid &&
                      t('invalidTradeRate')))
                  }
                  style={tradeRateProps?.style}
                  sx={tradeRateProps?.sx}
                  hideEditIcon={hideLocks}
                  editiconsize={tradeRateProps?.editiconsize}
                  {...conditionalProps('tradeRate')}
                />
              </Grid>
            </Grid>
              </Grid>
            </>
          ) : (
            <>
              <Grid
                item
                xs={4}
                padding="1rem"
                bgcolor={theme.wrapper.background.paper}
                borderRadius="0.5rem"
              >
                <Grid container spacing={5}>
                  <Grid item xs={12}>
                    <AryLockableTextField
                      name="tradeRate"
                      label={tradeRateLabel || t('Trade Rate')}
                      value={tradeRateProps?.tradeRate}
                      decimalPlaces={6}
                      fullWidth
                      disabled={
                        lockedLabel.value === 'tradeRate' ||
                        initialProps?.currencyCode ===
                          equivalentProps?.currencyCode ||
                        (initialAmountDisabled && equivalentAmountDisabled)
                      }
                      onChange={(event) => {
                        tradeRateProps?.tradeRateOnChange(event?.numberValue);
                      }}
                      onBlur={() => {
                        tradeRateProps?.onBlur();
                        if (
                          lockedLabel.value === currencyGroupComponents[0] &&
                          tradeRateProps?.tradeRate === 1
                        ) {
                          equivalentProps.equivalentCurrencyCodeOnChange();
                          equivalentProps.equivalentAmountValueOnChange();
                        }
                        if (
                          lockedLabel.value === currencyGroupComponents[2] &&
                          tradeRateProps?.tradeRate === 1
                        ) {
                          initialProps.initialCurrencyCodeOnChange();
                          initialProps.initialAmountValueOnChange();
                        }
                      }}
                      error={
                        !shouldIgnoreTradeRateError && ((initialProps?.amountValue &&
                          equivalentProps?.amountValue &&
                          tradeRateProps?.tradeRate <= 0) ||
                        (initialProps?.amountValue &&
                          equivalentProps?.amountValue &&
                          !isTradeRateValid))
                      }
                      helperText={
                        !shouldIgnoreTradeRateError && ((initialProps?.amountValue &&
                          equivalentProps?.amountValue &&
                          tradeRateProps?.tradeRate <= 0 &&
                          t('positiveTradeRate')) ||
                        (initialProps?.amountValue &&
                          equivalentProps?.amountValue &&
                          !isTradeRateValid &&
                          t('invalidTradeRate')))
                      }
                      style={tradeRateProps?.style}
                      sx={tradeRateProps?.sx}
                      hideEditIcon={hideLocks}
                      editiconsize={tradeRateProps?.editiconsize}
                      {...conditionalProps('tradeRate')}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid
                item
                xs={8}
                padding="1rem"
                paddingTop={initialProps?.isHidden ? '1rem' : '0'}
                bgcolor={theme.wrapper.background.paper}
                borderRadius="0.5rem"
                borderRight={`${theme.icon.disable.color} 0.0625rem dashed`}
              >
                <Grid container spacing={5}>
                  <Grid item xs={6}>
                    <AryCurrencyAutoComplete
                      value={
                        equivalentProps?.equivalentFixedCurrencyCode ||
                        equivalentProps?.currencyCode
                      }
                      onChange={equivalentProps?.currencyCodeOnChange}
                      label={`${t(equivalentProps?.currencyLabel_) || t('currency')}${
                        equivalentProps?.currencyCodeRequired ? ' *' : ''
                      }`}
                      disabled={
                        others.equivalentCurrencyDisabled ||
                        equivalentProps?.equivalentFixedCurrencyCode
                      }
                      {...equivalentProps?.currencyCodeAutoCompleteProps}
                      handleChangeDecimalPlaces={others.handleChangeDecimalPlaces}
                      excludedCurrencyCodes={equivalentProps?.excludedCurrencyCodes}
                      onlyCurrencyCodes={equivalentProps?.equivalentOnlyCurrencyCodes}
                      // getMirrorCurrencies={getMirrorCurrencies}
                      branchId={others.branchId}
                      maxheight={others.equivalentCurrencyMaxHeight}
                      apiType={others.apiType}
                      // handleChangeCurrencyCounts={handleChangeCurrencyCounts}
                      // error={error}
                      style={{
                        control: {
                          borderRadius: '0.25rem',
                          backgroundColor: `${theme.chip.blue.background} !important`,
                          ...equivalentProps?.currencyCodeSx?.control
                        },
                        dropdownIndicator: {
                          paddingTop: '0',
                          paddingBottom: '0',
                          ...equivalentProps?.currencyCodeSx?.dropdownIndicator
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <AryLockableTextField
                      fullWidth
                      removeAdditionalPadding
                      decimalPlaces={
                        equivalentCurrencyDecimalPlaces < 0
                          ? 0
                          : equivalentCurrencyDecimalPlaces
                      }
                      label={`${
                        equivalentProps?.equivalentAmountLabel || t('amount')
                      }`}
                      disabled={
                        lockedLabel.value === 'equivalent' ||
                        !(
                          equivalentProps?.currencyCode ||
                          equivalentProps?.equivalentFixedCurrencyCode
                        ) ||
                        equivalentAmountDisabled ||
                        (isNeutralTradeRate(tradeRateProps?.tradeRate) &&
                          isSameCurrencyPair(
                            initialProps?.currencyCode,
                            equivalentProps?.currencyCode
                          ))
                      }
                      error={equivalentProps?.amountError}
                      value={equivalentProps?.amountValue}
                      currencyCode={
                        equivalentProps?.equivalentFixedCurrencyCode ||
                        equivalentProps?.currencyCode
                      }
                      onChange={(event) => {
                        equivalentProps?.amountValueOnChange(event?.numberValue);
                      }}
                      style={{
                        borderRadius: '0.25rem'
                      }}
                      onBlur={equivalentProps?.onBlur}
                      helperText={equivalentProps?.amountHelperText}
                      sx={equivalentProps?.amountSx}
                      hideEditIcon={
                        (initialAmountDisabled && equivalentAmountDisabled) ||
                        hideLocks
                      }
                      {...conditionalProps('equivalent')}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </>
          )
          }

        {(!spotRate?.isHidden || !profitRate?.isHidden) && (
          <Grid
            item
            xs={4}
            padding="1rem"
            paddingTop="0"
            display="flex"
            alignItems="center"
            bgcolor={theme.wrapper.background.paper}
            borderRadius="0.5rem"
          >
          <Grid container justifyContent="space-between">
            <Grid item display="flex" alignItems="center">
              {spotRate?.isHidden && null}
              {!spotRate?.isHidden && (
                <SystemRate
                  baseCurrencyCode={initialProps?.currencyCode}
                  destinationCurrencyCode={equivalentProps?.currencyCode}
                  initialAmount={initialProps?.amountValue}
                />
              )}
            </Grid>
            <Grid item xs={6} display="flex" alignItems="center">
              {profitRate?.isHidden && null}
              {!profitRate?.isHidden && (
                <ProfitRate
                  sourceCurrencyCode={initialProps?.currencyCode}
                  sourceAmountValue={initialProps?.amountValue}
                  destinationCurrencyCode={equivalentProps?.currencyCode}
                  enteredTradeRate={tradeRateProps?.tradeRate}
                  isRateMinusEnteredRate={isRateMinusEnteredRate}
                  destinationAmountValue={equivalentProps?.amountValue}
                  profitRateProps={profitRateProps}
                />
              )}
            </Grid>
          </Grid>
          </Grid>
        )}
      </Grid>
      {!(initialAmountDisabled && equivalentAmountDisabled) && !hideLocks && (
        <AryTriplexComponentsLock
          open={popOver}
          anchorEl={anchorEl}
          lockedComponent={lockedLabel}
          setLockComponentOnChange={(newValue) => {
            setLockedLabel(newValue);
            setAnchorEl(null);
            setPopOver(false);
          }}
          handleClose={() => {
            setAnchorEl(null);
            setPopOver(false);
          }}
          components={lockableComponents}
        />
      )}
    </>
  );
};

export default AryTriplex;
